import { convertToDateTime } from '@reg/shared/helper/dateTime-helper';
import { IdentityOrganization, IdentitySeasonGroup, IdentitySport, RankingSettings } from '@reg/shared/model';
import { BodyAssessmentSettings } from '@reg/shared/model/identity/body-assessment-settings.model';
import { ResultSettings } from '@reg/shared/model/identity/result-settings.model';

/**
 * Model to hold season resource data from identity
 */
export class IdentitySeason {
    public id: string = null;
    public name: string = null;
    public organization: IdentityOrganization = new IdentityOrganization();
    public startDate: Date;
    public endDate: Date;
    public timeZone: string;
    public sport: IdentitySport = new IdentitySport();
    public isActive: boolean;
    public bodyAssessmentSettings: BodyAssessmentSettings = new BodyAssessmentSettings();
    public resultSettings: ResultSettings = new ResultSettings();
    public rankingSettings: RankingSettings = new RankingSettings();
    public groups: IdentitySeasonGroup[] = [];

    public get isWtManagementEnabled(): boolean {
        return !this.bodyAssessmentSettings.isEmpty();
    }

    public get isRankingsEnabled(): boolean {
        return !this.rankingSettings.isEmpty();
    }

    constructor(id: string = null) {
        this.id = id;
    }

    public formatNameOrg(): string {
        const orgName: string = this.organization && (this.organization.shortName || this.organization.longName);
        if (orgName) {
            return `${this.name} (${orgName})`;
        }

        return this.name;
    }

    public clone(): IdentitySeason {
        const clone: IdentitySeason = new IdentitySeason();
        clone.id = this.id;
        clone.name = this.name;
        clone.isActive = this.isActive;
        clone.timeZone = this.timeZone;
        clone.organization = this.organization && this.organization.clone();
        clone.sport = this.sport && this.sport.clone();
        clone.bodyAssessmentSettings = this.bodyAssessmentSettings && this.bodyAssessmentSettings.clone();
        clone.resultSettings = this.resultSettings && this.resultSettings.clone();
        clone.rankingSettings = this.rankingSettings && this.rankingSettings.clone();
        clone.groups = (this.groups || []) && this.groups.map((g: IdentitySeasonGroup) => g.clone());

        if (this.startDate) {
            clone.startDate = new Date(this.startDate.getTime());
        }
        if (this.endDate) {
            clone.endDate = new Date(this.endDate.getTime());
        }

        return clone;
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'season';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.name = this.name;
        json.data.attributes.startDate = this.startDate;
        json.data.attributes.endDate = this.endDate;
        json.data.attributes.timeZone = this.timeZone;

        if (this.bodyAssessmentSettings) {
            json.data.attributes.bodyAssessmentSettings = this.bodyAssessmentSettings.serialize();
        }
        if (this.resultSettings) {
            json.data.attributes.resultSettings = this.resultSettings.serialize();
        }
        if (this.rankingSettings) {
            json.data.attributes.rankingSettings = this.rankingSettings.serialize();
        }

        json.data.relationships = {};

        if (this.sport && this.sport.id) {
            json.data.relationships.sport = { data: { type: 'sport', id: this.sport.id } };
        }

        if (!this.id && this.organization && this.organization.id) {
            // Only set organization on updates
            json.data.relationships.organization = { data: { type: 'organization', id: this.organization.id } };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from json api format
     */
    public deserialize(json: any, included: any[] = null): IdentitySeason {
        const data: any = json.data || json;
        const attrs: any = data.attributes;
        included = included || json.included || [];

        this.id = data.id;
        this.name = attrs.name;
        this.startDate = convertToDateTime(attrs.startDate);
        this.endDate = convertToDateTime(attrs.endDate);
        this.isActive = attrs.isActive;
        this.timeZone = attrs.timeZone;
        this.organization = new IdentityOrganization(attrs.organizationId);
        this.sport = new IdentitySport(attrs.sportId);

        const organization: any = included.find((i: any) => i.type === 'organization' && i.id === attrs.organizationId);
        if (organization) {
            this.organization = (new IdentityOrganization()).deserialize(organization);
        }

        const sport: any = included.find((i: any) => i.type === 'sport' && i.id === attrs.sportId);
        if (sport) {
            this.sport = (new IdentitySport()).deserialize(sport);
        }

        this.bodyAssessmentSettings = new BodyAssessmentSettings().deserialize(attrs.bodyAssessmentSettings);
        this.resultSettings = new ResultSettings().deserialize(attrs.resultSettings);
        this.rankingSettings = new RankingSettings().deserialize(attrs.rankingSettings);

        return this;
    }
}
